import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { calendarRepository, processDeletedItems, cleanOverrideForApi } from '../repositories/calendar.repository';
import { pricingService } from '../services/pricing.service';
export const useAvailabilityStore = defineStore('availability', () => {
    const items = ref([]);
    const calendarId = ref(null);
    const serviceId = ref(null);
    const deletedItems = ref([]);
    const isLoading = ref(false);
    const error = ref(null);
    const isModified = ref(false);
    const lastBookingImpact = ref(null);
    const newItems = computed(() => items.value.filter((i) => i.isNew));
    const existingItems = computed(() => items.value.filter((i) => i.isExisting));
    const modifiedItems = computed(() => items.value.filter((i) => i.isModified));
    const availabilities = computed(() => items.value.filter((i) => i.type === 'availability'));
    const overrides = computed(() => items.value.filter((i) => i.type === 'override'));
    const hasChanges = computed(() => {
        return newItems.value.length > 0 || modifiedItems.value.length > 0 || deletedItems.value.length > 0;
    });
    const totalItems = computed(() => items.value.length);
    const canAddAvailability = computed(() => true);
    // Overrides can always be added - during creation they're stored locally and persisted after service save
    const canAddOverride = computed(() => true);
    async function loadForService(svcId) {
        isLoading.value = true;
        error.value = null;
        try {
            const result = await calendarRepository.getByServiceId(svcId);
            serviceId.value = svcId;
            calendarId.value = result.calendarId;
            items.value = result.items;
            deletedItems.value = [];
            lastBookingImpact.value = null;
            isModified.value = false;
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : 'Error loading availability';
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    function addAvailability(data) {
        items.value.push({
            ...data,
            type: 'availability',
            isNew: true,
            isExisting: false,
            isModified: false,
        });
        isModified.value = true;
    }
    function toNumber(value, fallback = 0) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
    }
    function toNullableNumber(value) {
        if (value === null || value === undefined || value === '')
            return null;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    function toTimeString(value) {
        if (typeof value === 'string')
            return value;
        if (value && typeof value === 'object' && typeof value.toString === 'function') {
            const str = value.toString();
            return str === '[object Object]' ? '' : str;
        }
        return '';
    }
    function calculateTotalPrice(schedule) {
        return pricingService.round(pricingService.calculateTotalPrice({
            netPrice: toNumber(schedule.net_price),
            commissionAmount: toNumber(schedule.commission_amount),
            commissionType: (schedule.commission_type || 'PERCENTAGE'),
            taxRate: toNullableNumber(schedule.tax_rate) ?? undefined,
        }));
    }
    function addOverride(data) {
        // Note: We allow adding overrides even without calendarId during service creation.
        // The calendar is auto-created when the service is created, and overrides will be
        // persisted after the service is saved via saveAll() or direct API calls.
        items.value.push({
            ...data,
            type: 'override',
            isNew: true,
            isExisting: false,
            isModified: false,
        });
        isModified.value = true;
    }
    function updateItem(index, data) {
        const item = items.value[index];
        if (!item) {
            return;
        }
        const shouldMarkModified = item.isExisting && !item.isNew;
        const updatedItem = {
            ...item,
            ...data,
            id: item.id,
            isExisting: item.isExisting,
            isNew: item.isNew,
            isModified: shouldMarkModified ? true : item.isModified,
        };
        items.value.splice(index, 1, updatedItem);
        isModified.value = true;
    }
    function removeItem(index) {
        const item = items.value[index];
        if (!item)
            return { success: false, error: 'Item not found' };
        if (item.id && item.isExisting) {
            deletedItems.value.push({
                id: item.id,
                type: item.type,
            });
        }
        items.value.splice(index, 1);
        isModified.value = true;
        return { success: true };
    }
    /**
     * Prepare availability rules for upsert from availability items.
     * Expands days_of_week into individual rules per day.
     */
    function prepareAvailabilityRulesFromItems(availabilityItems) {
        const rules = [];
        for (const item of availabilityItems) {
            // Only process availability type items (not overrides)
            if (item.type !== 'availability')
                continue;
            // Type narrowing: availability items have schedule array
            const scheduleArray = 'schedule' in item ? item.schedule : undefined;
            if (!scheduleArray || scheduleArray.length === 0)
                continue;
            for (const schedule of scheduleArray) {
                const daysOfWeek = schedule.days_of_week || [];
                const netPrice = toNumber(schedule.net_price);
                for (const day of daysOfWeek) {
                    rules.push({
                        day_of_week: day,
                        start_time: toTimeString(schedule.start_time),
                        end_time: toTimeString(schedule.end_time),
                        base_price: netPrice,
                        net_price: netPrice,
                        total_price: calculateTotalPrice(schedule),
                        price_type: schedule.price_type || 'PERSON',
                        commission_amount: toNumber(schedule.commission_amount),
                        commission_type: schedule.commission_type || 'PERCENTAGE',
                        standard_max_participants: toNumber(schedule.standard_max_participants, 1),
                        overcapacity_max_participants: toNullableNumber(schedule.overcapacity_max_participants),
                        overflow_price: toNullableNumber(schedule.overflow_price),
                        allow_reservations: schedule.allow_reservations !== false,
                    });
                }
            }
        }
        return rules.filter(r => r.day_of_week && r.start_time && r.end_time);
    }
    async function saveAll() {
        if (!serviceId.value) {
            throw new Error('No service ID set');
        }
        const newOverrides = newItems.value.filter((i) => i.type === 'override');
        // With new API, overrides are created via service-centric endpoint
        // Calendar is auto-created with service, so this check is less strict
        if (newOverrides.length > 0 && !serviceId.value) {
            throw new Error('No service ID. Cannot create overrides without a service.');
        }
        isLoading.value = true;
        error.value = null;
        try {
            const shouldFetchBookingImpact = hasChanges.value;
            // Process deleted items using service-centric endpoints
            const deletableItems = deletedItems.value.filter((d) => d.type === 'override');
            await processDeletedItems(serviceId.value, deletableItems);
            // Create new overrides using service-centric endpoint
            for (const item of newOverrides) {
                const cleaned = cleanOverrideForApi(item);
                await calendarRepository.createOverride(serviceId.value, cleaned);
            }
            // Handle new or modified AVAILABILITY items via UPSERT (PUT) - replaces all rules at once.
            // Also triggered when existing availability items were deleted, since upsert is the
            // only mechanism to remove them from the backend (no individual delete endpoint for rules).
            const changedAvailabilities = items.value.filter(i => i.type === 'availability' && (i.isNew || i.isModified));
            const availabilityDeletions = deletedItems.value.filter(d => d.type === 'availability');
            if (changedAvailabilities.length > 0 || availabilityDeletions.length > 0) {
                const allAvailabilities = items.value.filter(i => i.type === 'availability');
                const rules = prepareAvailabilityRulesFromItems(allAvailabilities);
                if (rules.length === 0) {
                    throw new Error('No valid weekly availability rules to save.');
                }
                await calendarRepository.upsertAvailability(serviceId.value, rules);
            }
            // Handle modified OVERRIDE items via individual PATCH
            const modifiedOverrides = modifiedItems.value.filter(i => i.type === 'override');
            for (const item of modifiedOverrides) {
                if (!item.id) {
                    throw new Error('Cannot update override without an ID.');
                }
                const cleaned = cleanOverrideForApi(item);
                // Use service-centric endpoint: /services/:serviceId/calendar/overrides/:id
                await calendarRepository.updateOverride(serviceId.value, item.id, cleaned);
            }
            deletedItems.value = [];
            isModified.value = false;
            await loadForService(serviceId.value);
            if (shouldFetchBookingImpact) {
                await refreshBookingImpact(serviceId.value);
            }
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : 'Error saving availability';
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function refreshBookingImpact(svcId = serviceId.value) {
        if (!svcId) {
            lastBookingImpact.value = null;
            return;
        }
        try {
            lastBookingImpact.value =
                await calendarRepository.getOutOfServiceBookings(svcId);
        }
        catch {
            lastBookingImpact.value = null;
        }
    }
    function reset() {
        items.value = [];
        calendarId.value = null;
        serviceId.value = null;
        deletedItems.value = [];
        isLoading.value = false;
        error.value = null;
        isModified.value = false;
        lastBookingImpact.value = null;
    }
    function $reset() {
        reset();
    }
    return {
        items,
        calendarId,
        serviceId,
        deletedItems,
        isLoading,
        error,
        isModified,
        lastBookingImpact,
        newItems,
        existingItems,
        modifiedItems,
        availabilities,
        overrides,
        hasChanges,
        totalItems,
        canAddAvailability,
        canAddOverride,
        loadForService,
        addAvailability,
        addOverride,
        updateItem,
        removeItem,
        saveAll,
        refreshBookingImpact,
        reset,
        $reset,
    };
});
